/**
 * CloudinarySettingsPanel — File Storage Configuration
 *
 * Add to PlatformSettings.js:
 *   import CloudinarySettingsPanel from './CloudinarySettingsPanel';
 *   // inside JSX:
 *   <hr className="my-5"/>
 *   <CloudinarySettingsPanel />
 *
 * API endpoints:
 *   GET  /superadmin/cloudinary-settings
 *   PUT  /superadmin/cloudinary-settings
 *   POST /superadmin/cloudinary-settings/test
 */

import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Badge, Spinner, Alert, InputGroup } from 'react-bootstrap';
import { HardDrive, Cloud, Eye, EyeOff, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';

const CloudinarySettingsPanel = () => {
    const [loading,   setLoading]  = useState(true);
    const [saving,    setSaving]   = useState(false);
    const [testing,   setTesting]  = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [showKey,   setShowKey]  = useState(false);
    const [showSec,   setShowSec]  = useState(false);

    const [form, setForm] = useState({
        file_storage_enabled:  true,
        file_storage_provider: 'local',
        cloudinary_cloud_name: '',
        cloudinary_api_key:    '',
        cloudinary_api_secret: '',
        cloudinary_folder:     'peoplesdock',
    });

    // Active provider shown in status badge
    const [activeProvider, setActiveProvider] = useState('local');

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    useEffect(() => {
        axiosInstance.get('/superadmin/cloudinary-settings')
            .then(r => {
                const d = r.data.data || {};
                setForm({
                    file_storage_enabled:  d.file_storage_enabled !== false,
                    file_storage_provider: d.file_storage_provider || 'local',
                    cloudinary_cloud_name: d.cloudinary_cloud_name || '',
                    cloudinary_api_key:    d.cloudinary_api_key    || '',
                    cloudinary_api_secret: d.cloudinary_api_secret || '',
                    cloudinary_folder:     d.cloudinary_folder     || 'peoplesdock',
                });
                setActiveProvider(d.active_provider || 'local');
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        setTestResult(null);
        try {
            await axiosInstance.put('/superadmin/cloudinary-settings', form);
            toast.success('File storage settings saved!');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save');
        } finally { setSaving(false); }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const res = await axiosInstance.post('/superadmin/cloudinary-settings/test');
            setTestResult({ ok: true, ...res.data.data });
            toast.success('Cloudinary connected ✓');
        } catch (err) {
            setTestResult({ ok: false, error: err.response?.data?.message || 'Connection failed' });
            toast.error(err.response?.data?.message || 'Test failed');
        } finally { setTesting(false); }
    };

    const isCloud = form.file_storage_provider === 'cloudinary';

    if (loading) return (
        <div className="text-center py-4">
            <Spinner animation="border" size="sm" variant="primary"/>
        </div>
    );

    return (
        <div>
            {/* ── Header ─────────────────────────────────────────────── */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Cloud size={20} className="text-info"/>
                        File Storage Settings
                    </h5>
                    <p className="text-muted small mb-0">
                        Control where avatars and documents are stored.
                        Currently active:{' '}
                        <Badge bg={activeProvider === 'cloudinary' ? 'success' : 'secondary'}>
                            {activeProvider === 'cloudinary' ? '☁ Cloudinary CDN' : '💾 Local Disk'}
                        </Badge>
                    </p>
                </div>
                <Form.Check
                    type="switch"
                    id="storage-enabled-switch"
                    checked={!!form.file_storage_enabled}
                    label={<span className="fw-bold">
                        {form.file_storage_enabled ? 'Enabled' : 'Disabled'}
                    </span>}
                    onChange={e => set('file_storage_enabled', e.target.checked)}
                />
            </div>

            {!form.file_storage_enabled && (
                <Alert variant="warning" className="small py-2 mb-4">
                    File storage is disabled. Uploads will fail. Keep this enabled.
                </Alert>
            )}

            {/* ── Provider selector ──────────────────────────────────── */}
            <Row className="g-3 mb-4">
                {/* Local Disk */}
                <Col xs={12} md={6}>
                    <div
                        onClick={() => set('file_storage_provider', 'local')}
                        style={{
                            border: `2px solid ${!isCloud ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '18px 20px', cursor: 'pointer',
                            background: !isCloud ? '#eef2ff' : '#fff',
                            transition: 'all 0.2s',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-2">
                                <HardDrive size={20} className="text-secondary"/>
                                <div>
                                    <div className="fw-bold">Local Disk</div>
                                    <div className="text-muted small">Server filesystem</div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="secondary" style={{ fontSize: 10 }}>Default</Badge>
                                {!isCloud && <CheckCircle size={16} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>No extra configuration needed</li>
                            <li>Works instantly in development</li>
                            <li>Files saved to <code>uploads/</code> folder</li>
                            <li className="text-danger">⚠ Files lost on Render redeploy</li>
                        </ul>
                    </div>
                </Col>

                {/* Cloudinary */}
                <Col xs={12} md={6}>
                    <div
                        onClick={() => set('file_storage_provider', 'cloudinary')}
                        style={{
                            border: `2px solid ${isCloud ? '#4f46e5' : '#e5e7eb'}`,
                            borderRadius: 12, padding: '18px 20px', cursor: 'pointer',
                            background: isCloud ? '#eef2ff' : '#fff',
                            transition: 'all 0.2s',
                        }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="d-flex align-items-center gap-2">
                                <Cloud size={20} className="text-info"/>
                                <div>
                                    <div className="fw-bold">Cloudinary</div>
                                    <div className="text-muted small">Cloud CDN storage</div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <Badge bg="success" style={{ fontSize: 10 }}>Recommended</Badge>
                                {isCloud && <CheckCircle size={16} color="#4f46e5"/>}
                            </div>
                        </div>
                        <ul className="small text-muted mb-0 ps-3">
                            <li>Files survive every redeploy</li>
                            <li>Global CDN — fast image loading</li>
                            <li>Auto face-crop for avatars</li>
                            <li className="text-success">Free: 25 GB storage, 25 GB/month</li>
                        </ul>
                    </div>
                </Col>
            </Row>

            {/* ── Cloudinary credentials ─────────────────────────────── */}
            {isCloud && (
                <Card className="border-0 shadow-sm p-4 mb-4">
                    <div className="fw-bold small text-uppercase text-muted mb-3">
                        Cloudinary Credentials
                    </div>
                    <Row className="g-3">
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                Cloud Name <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                placeholder="e.g. dxyz123abc"
                                value={form.cloudinary_cloud_name}
                                onChange={e => set('cloudinary_cloud_name', e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Found at{' '}
                                <a href="https://console.cloudinary.com" target="_blank" rel="noopener noreferrer">
                                    console.cloudinary.com
                                </a>{' '}
                                → Dashboard → Cloud Name
                            </Form.Text>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                API Key <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showKey ? 'text' : 'password'}
                                    placeholder="123456789012345"
                                    value={form.cloudinary_api_key}
                                    onChange={e => set('cloudinary_api_key', e.target.value)}
                                />
                                <Button variant="outline-secondary" type="button"
                                    onClick={() => setShowKey(p => !p)}>
                                    {showKey ? <EyeOff size={14}/> : <Eye size={14}/>}
                                </Button>
                            </InputGroup>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">
                                API Secret <span className="text-danger">*</span>
                            </Form.Label>
                            <InputGroup>
                                <Form.Control
                                    type={showSec ? 'text' : 'password'}
                                    placeholder="API Secret from Cloudinary dashboard"
                                    value={form.cloudinary_api_secret}
                                    onChange={e => set('cloudinary_api_secret', e.target.value)}
                                />
                                <Button variant="outline-secondary" type="button"
                                    onClick={() => setShowSec(p => !p)}>
                                    {showSec ? <EyeOff size={14}/> : <Eye size={14}/>}
                                </Button>
                            </InputGroup>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Folder Prefix</Form.Label>
                            <Form.Control
                                placeholder="peoplesdock"
                                value={form.cloudinary_folder}
                                onChange={e => set('cloudinary_folder', e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Files will be stored under <code>{form.cloudinary_folder || 'peoplesdock'}/avatars/</code>{' '}
                                and <code>{form.cloudinary_folder || 'peoplesdock'}/documents/</code>
                            </Form.Text>
                        </Col>
                    </Row>

                    {/* Setup guide */}
                    <Alert variant="info" className="mt-3 mb-0 py-2 small">
                        <strong>Quick setup (2 minutes):</strong>
                        <ol className="mb-0 mt-1 ps-3">
                            <li>Go to <a href="https://cloudinary.com/users/register_free" target="_blank" rel="noopener noreferrer">cloudinary.com</a> → sign up free</li>
                            <li>Dashboard shows Cloud Name, API Key, API Secret</li>
                            <li>Copy all three above → Save → Test Connection</li>
                            <li>Run <code>npm install cloudinary multer-storage-cloudinary</code> in your backend</li>
                        </ol>
                    </Alert>
                </Card>
            )}

            {/* Test result */}
            {testResult && (
                <Alert variant={testResult.ok ? 'success' : 'danger'} className="small py-2 mb-3">
                    {testResult.ok ? (
                        <>
                            ✓ Connected to Cloudinary
                            {testResult.plan && <> · Plan: <strong>{testResult.plan}</strong></>}
                            {testResult.storage_used_mb && testResult.storage_used_mb !== '—' && (
                                <> · Storage used: <strong>{testResult.storage_used_mb} MB</strong></>
                            )}
                        </>
                    ) : (
                        <>✗ {testResult.error}</>
                    )}
                </Alert>
            )}

            {/* ── Action buttons ─────────────────────────────────────── */}
            <div className="d-flex gap-3 align-items-center flex-wrap">
                <Button variant="primary" onClick={save} disabled={saving}>
                    {saving ? <><Spinner size="sm" className="me-2"/>Saving…</> : '💾 Save Settings'}
                </Button>

                {isCloud && (
                    <Button variant="outline-info" onClick={test} disabled={testing}>
                        {testing
                            ? <><Spinner size="sm" className="me-2"/>Testing…</>
                            : <><RefreshCw size={13} className="me-1"/>Test Connection</>}
                    </Button>
                )}
            </div>

            {/* Info box */}
            <Alert variant="light" className="mt-4 mb-0 border small">
                <strong>How it works:</strong> When Cloudinary is enabled and saved,
                all new avatar and document uploads automatically go to Cloudinary CDN.
                Existing files on local disk are not migrated automatically.
                Switching back to Local Disk will use the server filesystem for new uploads.
            </Alert>
        </div>
    );
};

export default CloudinarySettingsPanel;
